import { MessageSquare } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Avatar } from '@/components/ui/avatar'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChannelMark } from '@/components/channel-icons'
import {
  formatDate,
  formatTime,
  formatCurrency,
  getChannelLabel,
  getChannelBadgeVariant,
  getStatusBadgeVariant,
} from '@/lib/format-utils'

/**
 * Shape of the Uplisting-sourced reservation data this sidebar reads - only
 * the fields it actually consumes, mirroring `uplistingData` usage in
 * src/pages/reservation-details.jsx.
 */
interface UplistingData {
  preferred_guest_name?: string
  guest_name?: string
  number_of_guests?: number | null
  check_in?: string
  check_out?: string
  arrival_time?: string
  departure_time?: string
  status?: string
  channel?: string
  total_payout?: number
  lock_code?: string | null
  note?: string | null
}

interface Reservation {
  id?: string
  reservation_id?: string
  guest_name?: string
  check_in?: string
  check_out?: string
  status?: string
  channel?: string
  external?: { uplisting?: UplistingData } | null
}

interface ReservationSidebarProps {
  reservation?: Reservation | null
  showNoThreadAction?: boolean
}

/**
 * Always-populated reservation summary sidebar for the new inbox view
 * (see docs/archive/messages-redesign-plan.md - "Right pane").
 *
 * Purely presentational: renders whatever reservation object it is given.
 * The caller (wiring slice) is responsible for always having a reservation
 * selected - see "Resolved decisions" #2 in docs/archive/messages-redesign-pr-slices.md
 * for the no-threads and empty-property fallback rules this component does
 * not itself implement.
 *
 * Field names mirror src/pages/reservation-details.jsx's uplistingData usage:
 * single booker name + headcount (no fabricated co-travelers), real
 * `lock_code` / `note` / `total_payout` fields sourced from Uplisting, guarded
 * for absence rather than showing a blank or fake value.
 *
 * `showNoThreadAction` (Codex PR review on the calendar-jump slice): when a
 * calendar bar click resolves to "this reservation has no thread yet"
 * (docs/archive/messages-redesign-plan.md's calendar flow - "the button reads 'No
 * messages yet' (disabled)"), the caller passes this so the sidebar carries
 * that specific signal, distinct from the generic thread-pane empty state
 * shown for the ordinary (non-calendar) no-threads-yet fallback. Disabled,
 * not wired to any action - there is genuinely nothing to click yet (root
 * CLAUDE.md: "Do not show UI controls that have no backend behavior behind
 * them" is satisfied by disabling it, not by omitting it here).
 */
export function ReservationSidebar({
  reservation,
  showNoThreadAction = false,
}: ReservationSidebarProps) {
  const uplistingData = reservation?.external?.uplisting || {}

  const guestDisplayName =
    uplistingData.preferred_guest_name ||
    reservation?.guest_name ||
    uplistingData.guest_name ||
    'Guest'

  const guests = uplistingData.number_of_guests || null
  const checkIn = reservation?.check_in || uplistingData.check_in
  const checkOut = reservation?.check_out || uplistingData.check_out
  const checkInTime = uplistingData.arrival_time
  const checkOutTime = uplistingData.departure_time
  const status = uplistingData.status || reservation?.status || null
  const channel = reservation?.channel || uplistingData.channel || null
  const totalPayout = uplistingData.total_payout
  const lockCode = uplistingData.lock_code
  const note = uplistingData.note
  const hasNote = note && note.trim().length > 0

  return (
    <div className="space-y-4" data-testid="reservation-sidebar">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-center gap-3">
              <Avatar
                name={guestDisplayName}
                seed={reservation?.id || reservation?.reservation_id}
              />
              <CardTitle className="truncate">{guestDisplayName}</CardTitle>
            </div>
            {showNoThreadAction && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled
                title="No conversation exists yet for this reservation"
              >
                <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                No messages yet
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {guests && (
              <Badge variant="secondary" className="text-xs">
                {guests} guest{guests !== 1 ? 's' : ''}
              </Badge>
            )}
            {channel && (
              <Badge
                variant={getChannelBadgeVariant(channel) as BadgeProps['variant']}
                className="text-xs gap-1"
              >
                <ChannelMark channel={channel} className="h-3.5 w-3.5" />
                {getChannelLabel(channel)}
              </Badge>
            )}
            {status && (
              <Badge
                variant={getStatusBadgeVariant(status) as BadgeProps['variant']}
                className="text-xs"
              >
                {status.replace(/_/g, ' ')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-input border border-border bg-surface2 p-2.5">
              <div className="text-xs text-textMuted">Check-in</div>
              <div className="text-sm font-medium">{formatDate(checkIn ?? '')}</div>
              {checkInTime && (
                <div className="text-xs text-textMuted">{formatTime(checkInTime)}</div>
              )}
            </div>
            <div className="rounded-input border border-border bg-surface2 p-2.5">
              <div className="text-xs text-textMuted">Checkout</div>
              <div className="text-sm font-medium">{formatDate(checkOut ?? '')}</div>
              {checkOutTime && (
                <div className="text-xs text-textMuted">{formatTime(checkOutTime)}</div>
              )}
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-textMuted">Door code</span>
            <span className="text-sm font-medium">
              {lockCode ? lockCode : 'Not set in Uplisting'}
            </span>
          </div>
          {totalPayout !== undefined && (
            <div className="flex justify-between items-center pt-2 border-t border-border mt-2">
              <span className="text-sm font-medium">Total payout</span>
              <span className="font-bold text-base">{formatCurrency(totalPayout)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {hasNote && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Note from Uplisting</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{note}</div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
